import { makeAutoObservable, runInAction } from 'mobx'
import { apiClient } from '@/services/api'
import type { Collection, CollectionCreate, Document, SearchResult } from '@/types'

class KnowledgeBaseStore {
  collections: string[] = []
  selectedCollection: Collection | null = null
  searchResults: SearchResult[] = []
  isLoading = false
  error: string | null = null

  // Search state
  searchQuery = ''
  searchLimit = 10
  isSearching = false

  // Upload state
  isUploading = false
  uploadProgress = 0

  constructor() {
    makeAutoObservable(this)
  }

  // Computed values
  get hasCollections() {
    return this.collections.length > 0
  }

  get selectedCollectionName() {
    return this.selectedCollection?.name || null
  }

  // Actions
  async fetchCollections() {
    this.isLoading = true
    this.error = null

    try {
      const collections = await apiClient.getCollections()

      runInAction(() => {
        this.collections = collections
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch collections'
        this.isLoading = false
      })
    }
  }

  async fetchCollection(name: string) {
    this.isLoading = true
    this.error = null

    try {
      const collection = await apiClient.getCollection(name)

      runInAction(() => {
        this.selectedCollection = collection
        this.isLoading = false
      })

      return collection
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to fetch collection'
        this.isLoading = false
      })
      return null
    }
  }

  async createCollection(data: CollectionCreate) {
    this.isLoading = true
    this.error = null

    try {
      const collection = await apiClient.createCollection(data)

      runInAction(() => {
        this.collections = [...this.collections, collection.name]
        this.selectedCollection = collection
        this.isLoading = false
      })

      return collection
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to create collection'
        this.isLoading = false
      })
      throw error
    }
  }

  async deleteCollection(name: string) {
    this.isLoading = true
    this.error = null

    try {
      await apiClient.deleteCollection(name)

      runInAction(() => {
        this.collections = this.collections.filter((c) => c !== name)
        if (this.selectedCollection?.name === name) {
          this.selectedCollection = null
        }
        this.isLoading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to delete collection'
        this.isLoading = false
      })
      throw error
    }
  }

  async insertDocuments(collectionName: string, documents: Document[]) {
    this.isUploading = true
    this.uploadProgress = 0
    this.error = null

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        runInAction(() => {
          if (this.uploadProgress < 90) {
            this.uploadProgress += 10
          }
        })
      }, 200)

      const result = await apiClient.insertDocuments(collectionName, documents)

      clearInterval(progressInterval)

      runInAction(() => {
        this.uploadProgress = 100
        this.isUploading = false
        // Refresh collection to get updated count
        this.fetchCollection(collectionName)
      })

      return result
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to insert documents'
        this.isUploading = false
        this.uploadProgress = 0
      })
      throw error
    }
  }

  async searchCollection(collectionName: string, query: string, limit: number = 10) {
    this.isSearching = true
    this.error = null

    try {
      const response = await apiClient.searchDocuments(collectionName, query, { limit })

      runInAction(() => {
        this.searchResults = response.results
        this.searchQuery = query
        this.searchLimit = limit
        this.isSearching = false
      })

      return response.results
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.detail || 'Failed to search collection'
        this.isSearching = false
      })
      throw error
    }
  }

  // Utility actions
  clearSearch() {
    this.searchQuery = ''
    this.searchResults = []
  }

  clearError() {
    this.error = null
  }

  clearSelectedCollection() {
    this.selectedCollection = null
    this.searchResults = []
    this.searchQuery = ''
  }
}

export const knowledgeBaseStore = new KnowledgeBaseStore()
